// The trailing `formula` argument convention for leaf scorers (design sections 0.5 and 2.9).
//
// Revision 2 kernels reuse the existing leaf scorers (surf-scoring-v5.ts, sailing-scoring.ts,
// mtb-scoring.ts and the rest) in one of three ways:
//   1. exported pure helpers;
//   2. an optional TRAILING `formula` argument on the leaf scorer, whose default reproduces
//      revision 1 exactly;
//   3. new helper modules for structural changes (swell-partitions.ts, wave-orbital.ts,
//      surf-zone.ts, mtb-precip.ts, offshore-sector.ts).
//
// Rules for (2), so the revision 1 wrap never moves:
// - The argument is the LAST parameter, optional, typed `LeafFormula<Options>`, and omitted or
//   `REVISION_ONE_FORMULA` means revision 1 behaviour, bit for bit.
// - Every revision 1 caller (the legacy route, backfill, enrichment, precompute and the revision
//   1 kernels) keeps calling without it. Only revision 2 kernels pass one.
// - Options are data, never functions, so they hash into the kernel's params fingerprint:
//   kernels put the formula they pass into `SportKernel.params`.
// - Each leaf file has exactly one owning package (design 7.1); the revision 1 output hashes in
//   engines/core/__tests__/leaf-revision-one.test.ts guard every default.
//
// Example (WP-S2, downwind-foil-scoring.ts):
//   export type DownwindFoilFormula = LeafFormula<{ windBand: "legacy" | "knots" }>;
//   export function calculateDownwindFoilHourly(input: DownwindFoilInput, formula: DownwindFoilFormula = REVISION_ONE_FORMULA) {
//     const knots = formula.revision >= 2 && formula.windBand === "knots";
//     ...
//   }

/**
 * A leaf formula: its revision plus optional option fields (all data). The default carries no
 * options (Record<never, never>: a string index of never would make `revision` unsatisfiable).
 */
export type LeafFormula<TOptions extends Readonly<Record<string, unknown>> = Readonly<Record<never, never>>> =
  Readonly<{ revision: number }> & Readonly<Partial<TOptions>>;

/** The default of every trailing formula argument: revision 1 behaviour. */
export const REVISION_ONE_FORMULA: Readonly<{ revision: 1 }> = Object.freeze({ revision: 1 });

/** The formula's revision, 1 when omitted. */
export function leafFormulaRevision(formula?: Readonly<{ revision: number }> | null): number {
  return formula?.revision ?? 1;
}

/** True when a leaf must behave exactly as revision 1. */
export function isRevisionOneFormula(formula?: Readonly<{ revision: number }> | null): boolean {
  return leafFormulaRevision(formula) === 1;
}
